import React from 'react';
import { useNavigate } from 'react-router-dom';
import { track } from '../utils/analytics';

const STORAGE_KEY = 'guruone_strategy';

function loadData() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const all = JSON.parse(raw);
    const user = (() => { try { return JSON.parse(localStorage.getItem('guruone_user')); } catch { return null; } })();
    const key = user?.id ? `user_${user.id}` : null;
    if (key && all[key]) return all[key];
    const entries = Object.values(all).filter(v => typeof v === 'object' && v !== null);
    return entries.reduce((best, curr) => Object.keys(curr).length > Object.keys(best).length ? curr : best, {});
  } catch { return {}; }
}

function str(obj, key, fallback = '') {
  if (!obj) return fallback;
  const v = obj[key];
  if (!v) return fallback;
  if (Array.isArray(v)) return v.filter(Boolean).join(' · ') || fallback;
  return String(v).trim() || fallback;
}

const PRINT_CSS = `
@media print {
  @page { size: A3 landscape; margin: 0; }
  body { margin: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .no-print { display: none !important; }
}
`;

// ── Large infographic SVG icons ───────────────────────────────────

function IconMission({ size = 72 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 72 72" fill="none">
      {/* Crosshair target */}
      <circle cx="36" cy="36" r="30" stroke="#1D9E75" strokeWidth="2" fill="none" opacity="0.15"/>
      <circle cx="36" cy="36" r="20" stroke="#1D9E75" strokeWidth="2" fill="none" opacity="0.3"/>
      <circle cx="36" cy="36" r="10" stroke="#1D9E75" strokeWidth="2" fill="none" opacity="0.5"/>
      <circle cx="36" cy="36" r="4" fill="#1D9E75"/>
      {/* Crosshair lines */}
      <line x1="36" y1="2" x2="36" y2="14" stroke="#1D9E75" strokeWidth="2.5" strokeLinecap="round"/>
      <line x1="36" y1="58" x2="36" y2="70" stroke="#1D9E75" strokeWidth="2.5" strokeLinecap="round"/>
      <line x1="2" y1="36" x2="14" y2="36" stroke="#1D9E75" strokeWidth="2.5" strokeLinecap="round"/>
      <line x1="58" y1="36" x2="70" y2="36" stroke="#1D9E75" strokeWidth="2.5" strokeLinecap="round"/>
    </svg>
  );
}

function IconVision({ size = 72 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 72 72" fill="none">
      {/* Star/compass with rays */}
      <circle cx="36" cy="36" r="8" fill="#7C3AED" opacity="0.2"/>
      <circle cx="36" cy="36" r="5" fill="#7C3AED"/>
      {/* 8 rays */}
      {[0,45,90,135,180,225,270,315].map((deg, i) => {
        const r = deg * Math.PI / 180;
        const short = i % 2 === 0 ? 0.45 : 0.55;
        const x1 = 36 + 10 * Math.sin(r); const y1 = 36 - 10 * Math.cos(r);
        const x2 = 36 + 32 * short * Math.sin(r); const y2 = 36 - 32 * short * Math.cos(r);
        return <line key={deg} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#7C3AED" strokeWidth={i % 2 === 0 ? 2.5 : 1.5} strokeLinecap="round" opacity={i % 2 === 0 ? 1 : 0.5}/>;
      })}
      <circle cx="36" cy="36" r="18" stroke="#7C3AED" strokeWidth="1" fill="none" opacity="0.2" strokeDasharray="3 4"/>
    </svg>
  );
}


function IconProp({ size = 72 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 72 72" fill="none">
      {/* Diamond with inner glow */}
      <path d="M36 8 L62 36 L36 64 L10 36 Z" stroke="#185FA5" strokeWidth="2" fill="#185FA5" fillOpacity="0.08"/>
      <path d="M36 18 L54 36 L36 54 L18 36 Z" stroke="#185FA5" strokeWidth="1.5" fill="#185FA5" fillOpacity="0.12"/>
      <path d="M36 26 L46 36 L36 46 L26 36 Z" fill="#185FA5" opacity="0.6"/>
      <circle cx="36" cy="36" r="4" fill="#185FA5"/>
      {/* Corner dots */}
      <circle cx="36" cy="8" r="2.5" fill="#185FA5" opacity="0.5"/>
      <circle cx="62" cy="36" r="2.5" fill="#185FA5" opacity="0.5"/>
      <circle cx="36" cy="64" r="2.5" fill="#185FA5" opacity="0.5"/>
      <circle cx="10" cy="36" r="2.5" fill="#185FA5" opacity="0.5"/>
    </svg>
  );
}

function IconCustomer({ size = 72 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 72 72" fill="none">
      {/* Central large person */}
      <circle cx="36" cy="22" r="10" stroke="#D97706" strokeWidth="2" fill="#D97706" fillOpacity="0.15"/>
      <path d="M18 58 C18 44 54 44 54 58" stroke="#D97706" strokeWidth="2" fill="#D97706" fillOpacity="0.1" strokeLinecap="round"/>
      {/* Smaller satellite people */}
      <circle cx="14" cy="32" r="7" stroke="#D97706" strokeWidth="1.5" fill="#D97706" fillOpacity="0.08" opacity="0.7"/>
      <path d="M5 52 C5 45 23 45 23 52" stroke="#D97706" strokeWidth="1.5" fill="none" opacity="0.5" strokeLinecap="round"/>
      <circle cx="58" cy="32" r="7" stroke="#D97706" strokeWidth="1.5" fill="#D97706" fillOpacity="0.08" opacity="0.7"/>
      <path d="M49 52 C49 45 67 45 67 52" stroke="#D97706" strokeWidth="1.5" fill="none" opacity="0.5" strokeLinecap="round"/>
      {/* Connection lines */}
      <line x1="36" y1="32" x2="20" y2="36" stroke="#D97706" strokeWidth="1" strokeDasharray="3 2" opacity="0.4"/>
      <line x1="36" y1="32" x2="52" y2="36" stroke="#D97706" strokeWidth="1" strokeDasharray="3 2" opacity="0.4"/>
    </svg>
  );
}

function IconDiff({ size = 72 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 72 72" fill="none">
      {/* Three overlapping circles (Venn) with highlight */}
      <circle cx="28" cy="32" r="18" stroke="#C0556E" strokeWidth="1.5" fill="#C0556E" fillOpacity="0.08"/>
      <circle cx="44" cy="32" r="18" stroke="#C0556E" strokeWidth="1.5" fill="#C0556E" fillOpacity="0.08"/>
      <circle cx="36" cy="46" r="18" stroke="#C0556E" strokeWidth="1.5" fill="#C0556E" fillOpacity="0.08"/>
      {/* Center star — unique overlap */}
      <circle cx="36" cy="36" r="7" fill="#C0556E" opacity="0.9"/>
      <path d="M36 30 L37.5 34.5 L42 34.5 L38.5 37 L40 42 L36 39 L32 42 L33.5 37 L30 34.5 L34.5 34.5 Z" fill="white"/>
    </svg>
  );
}

function IconGTM({ size = 72 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 72 72" fill="none">
      {/* Rocket */}
      <path d="M36 10 C36 10 50 18 50 36 L50 52 L36 62 L22 52 L22 36 C22 18 36 10 36 10Z" stroke="#1D9E75" strokeWidth="2" fill="#1D9E75" fillOpacity="0.1"/>
      <circle cx="36" cy="34" r="7" stroke="#1D9E75" strokeWidth="2" fill="#1D9E75" fillOpacity="0.2"/>
      {/* Exhaust flames */}
      <path d="M28 52 C24 56 22 62 26 64 C26 60 32 58 36 62 C40 58 46 60 46 64 C50 62 48 56 44 52" fill="#1D9E75" opacity="0.3"/>
      {/* Side fins */}
      <path d="M22 40 L14 50 L22 48 Z" fill="#1D9E75" opacity="0.4"/>
      <path d="M50 40 L58 50 L50 48 Z" fill="#1D9E75" opacity="0.4"/>
    </svg>
  );
}

// ── Value hexagons ────────────────────────────────────────────────
function ValueHexCluster({ values }) {
  const colours = ['#7C3AED','#185FA5','#1D9E75','#C0556E','#D97706','#9B6B3A'];
  const positions = [
    [110, 40], [175, 40], [240, 40],
    [142, 95], [208, 95],
  ];
  const display = values.slice(0, 5);

  function hexPath(cx, cy, r) {
    const pts = Array.from({ length: 6 }, (_, i) => {
      const a = (Math.PI / 180) * (60 * i - 30);
      return `${cx + r * Math.cos(a)},${cy + r * Math.sin(a)}`;
    });
    return `M${pts.join('L')}Z`;
  }

  return (
    <svg width="350" height="145" viewBox="0 0 350 145">
      {display.map((v, i) => {
        const [cx, cy] = positions[i] || [110 + i * 65, 40];
        const c = colours[i % colours.length];
        return (
          <g key={i}>
            <path d={hexPath(cx, cy, 42)} fill={c} fillOpacity="0.08" stroke={c} strokeWidth="1.5"/>
            <text x={cx} y={cy - 6} textAnchor="middle" fontSize="10" fontWeight="700" fill={c}>{v}</text>
            <line x1={cx - 18} y1={cy + 4} x2={cx + 18} y2={cy + 4} stroke={c} strokeWidth="1" opacity="0.3"/>
          </g>
        );
      })}
      {values.length === 0 && (
        <text x="175" y="75" textAnchor="middle" fontSize="12" fill="#ccc" fontStyle="italic">Values not yet defined</text>
      )}
    </svg>
  );
}

// ── Label pill ────────────────────────────────────────────────────
function Pill({ text, colour }) {
  return (
    <span style={{ display: 'inline-block', fontSize: '0.6rem', fontWeight: 700, padding: '2px 9px', borderRadius: 20, background: colour + '18', color: colour, border: `1px solid ${colour}44`, marginRight: 4, marginBottom: 3 }}>
      {text}
    </span>
  );
}

// ── Section block ─────────────────────────────────────────────────
function Block({ children, style }) {
  return (
    <div style={{ padding: '1.25rem 1.5rem', ...style }}>
      {children}
    </div>
  );
}

function SectionTitle({ colour, label }) {
  return (
    <div style={{ fontSize: '0.55rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.12em', color: colour, marginBottom: '0.5rem' }}>
      {label}
    </div>
  );
}

// ── One Page Strategy ─────────────────────────────────────────────
export default function OnePageStrategy() {
  const navigate = useNavigate();
  const data = loadData();

  const user = (() => { try { return JSON.parse(localStorage.getItem('guruone_user')); } catch { return null; } })();
  const businessName = user?.businessName || 'Our Strategy';
  const today = new Date().toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });

  const mission     = data.mission     || {};
  const vision      = data.vision      || {};
  const vp          = data.value_proposition || {};
  const cust        = data.customers?.entries   || [];
  const diffEntries = data.differentiators?.entries || [];
  const gtm         = data.go_to_market || {};
  const challenges  = data.current_challenges?.entries || [];
  const valEntries  = data.values?.entries || [];
  const founderP    = data.founder_purpose || {};

  // Values — extract value_theme from each entry for the hex cluster
  const valueList = valEntries.map(e => e.value_theme).filter(Boolean);
  // Non-negotiable values for the footnote quote
  const nonNegotiableValues = valEntries
    .filter(e => e.non_negotiable === 'Yes' && e.value_theme)
    .map(e => e.value_theme)
    .slice(0, 3);

  // Mission sentence
  const missionText = str(mission, 'primary_beneficiary')
    ? `We exist to help ${str(mission, 'primary_beneficiary').toLowerCase()}${str(mission, 'core_objective_themes') ? ` achieve ${str(mission, 'core_objective_themes').toLowerCase()}` : ''}.`
    : str(mission, 'core_objective_themes') || '';

  const visionText = str(vision, 'vision_statement') || str(vision, 'target_detail') || '';

  // Differentiators — from new per-entry structure
  // Lead differentiator first, then rest up to 3 total
  const sortedDiff = [
    ...diffEntries.filter(e => e.is_lead === 'Yes'),
    ...diffEntries.filter(e => e.is_lead !== 'Yes'),
  ].slice(0, 3);

  // Go-to-market primary motion — derive from primary_motion or blend
  const gtmPrimaryMotion = str(gtm, 'primary_motion') || (() => {
    const blend = gtm.acquisition_motion_blend;
    if (!blend) return '';
    const splits = blend.splits || blend;
    const top = Object.entries(splits).sort(([,a],[,b]) => b - a)[0];
    if (!top) return '';
    return top[0].replace(/_/g, ' ');
  })();

  // Acquisition blend as readable string
  const blendStr = (() => {
    const blend = gtm.acquisition_motion_blend;
    if (!blend) return '';
    const splits = blend.splits || (typeof blend === 'object' ? blend : null);
    if (!splits) return '';
    return Object.entries(splits)
      .filter(([,v]) => v > 0)
      .sort(([,a],[,b]) => b - a)
      .map(([k,v]) => `${k.replace(/_/g,' ')} ${v}%`)
      .join(' · ');
  })();

  return (
    <div style={{ minHeight: '100vh', background: '#e5e5e5', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>
      <style>{PRINT_CSS}</style>

      {/* Toolbar */}
      <div className="no-print" style={{ background: '#1a1a2e', padding: '0.65rem 1.5rem', display: 'flex', alignItems: 'center', gap: '1rem', position: 'sticky', top: 0, zIndex: 100 }}>
        <button onClick={() => navigate('/strategy')}
          style={{ background: 'none', border: '1px solid rgba(255,255,255,0.3)', color: 'rgba(255,255,255,0.7)', fontSize: '0.75rem', padding: '0.3rem 0.85rem', borderRadius: 20, cursor: 'pointer', width: 'auto', fontFamily: 'inherit' }}>
          ← Back to Canvas
        </button>
        <span style={{ color: 'white', fontSize: '0.9rem', fontWeight: 600, flex: 1 }}>
          Guru<strong style={{ color: '#1D9E75' }}>One</strong> — One Page Strategy
        </span>
        <span style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)', marginRight: '0.5rem' }}>Print → A3 Landscape → Save as PDF</span>
        <button onClick={() => { track('one_page_strategy_downloaded'); window.print(); }}
          style={{ background: '#1D9E75', border: 'none', color: 'white', fontSize: '0.82rem', fontWeight: 700, padding: '0.4rem 1.25rem', borderRadius: 20, cursor: 'pointer', width: 'auto', fontFamily: 'inherit' }}>
          ⬇ Print / Save PDF
        </button>
      </div>

      {/* POSTER */}
      <div style={{ padding: '2rem', display: 'flex', justifyContent: 'center' }}>
        <div style={{
          width: 1122,
          background: 'white',
          borderRadius: 12,
          overflow: 'hidden',
          boxShadow: '0 8px 48px rgba(0,0,0,0.16)',
        }}>

          {/* ── MASTHEAD ──────────────────────────────────────────── */}
          <div style={{ background: '#1a1a2e', padding: '1.5rem 2.5rem', display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: '0.55rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.15em', color: '#1D9E75', marginBottom: '0.35rem' }}>One Page Strategy</div>
              <div style={{ fontSize: '2rem', fontWeight: 800, color: 'white', letterSpacing: '-0.03em', lineHeight: 1 }}>{businessName}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.25)', letterSpacing: '0.04em' }}>{today} · Confidential</div>
              <div style={{ fontSize: '0.5rem', color: 'rgba(255,255,255,0.12)', marginTop: '0.15rem' }}>Powered by GuruOne · InPragmia Consulting</div>
            </div>
          </div>

          {/* ── BODY ─────────────────────────────────────────────── */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1px 1fr 1px 1fr 1px 1fr', minHeight: 560 }}>

            {/* DIVIDERS */}
            {[1, 3, 5].map(i => (
              <div key={i} style={{ background: '#f0f0f0', gridColumn: i + 1 }} />
            ))}

            {/* ── COL 1: Mission + Values ─────────────────────────── */}
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {/* Mission */}
              <Block style={{ flex: 1, borderBottom: '1px solid #f5f5f5' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', marginBottom: '0.85rem' }}>
                  <IconMission size={64} />
                  <div>
                    <SectionTitle colour="#0D8C8C" label="Mission" />
                    <p style={{ fontSize: '0.82rem', fontWeight: 600, color: '#1a1a2e', lineHeight: 1.6, margin: 0 }}>
                      {missionText || <span style={{ color: '#ccc', fontStyle: 'italic', fontWeight: 400 }}>Not yet defined</span>}
                    </p>
                    <div style={{ marginTop: '0.5rem', display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                      {str(mission, 'success_definition') && <Pill text={`Success = ${str(mission, 'success_definition')}`} colour="#0D8C8C" />}
                      {str(founderP, 'time_horizon') && <Pill text={str(founderP, 'time_horizon')} colour="#C0556E" />}
                    </div>
                  </div>
                </div>
              </Block>

              {/* Values */}
              <Block style={{ flex: 1.2 }}>
                <SectionTitle colour="#7F77DD" label="Values" />
                <ValueHexCluster values={valueList} />
                {nonNegotiableValues.length > 0 && (
                  <p style={{ fontSize: '0.65rem', color: '#888', lineHeight: 1.5, margin: '0.5rem 0 0', borderTop: '1px solid #f0f0f0', paddingTop: '0.5rem', fontStyle: 'italic' }}>
                    Non-negotiable: {nonNegotiableValues.join(' · ')}
                  </p>
                )}
              </Block>
            </div>

            {/* ── COL 2: Vision + Value Prop ──────────────────────── */}
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {/* Vision */}
              <Block style={{ flex: 1, borderBottom: '1px solid #f5f5f5', background: '#fafbff' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', marginBottom: '0.85rem' }}>
                  <IconVision size={64} />
                  <div style={{ flex: 1 }}>
                    <SectionTitle colour="#7C3AED" label="Vision" />
                    {visionText ? (
                      <p style={{ fontSize: '0.88rem', fontWeight: 700, color: '#1a1a2e', lineHeight: 1.55, margin: '0 0 0.5rem', fontStyle: 'italic' }}>
                        "{visionText}"
                      </p>
                    ) : (
                      <p style={{ fontSize: '0.82rem', color: '#ccc', fontStyle: 'italic', margin: 0 }}>Not yet defined</p>
                    )}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                      {str(vision, 'target_type') && <Pill text={str(vision, 'target_type')} colour="#7C3AED" />}
                      {str(vision, 'time_horizon_years') && <Pill text={`${str(vision, 'time_horizon_years')} year target`} colour="#7C3AED" />}
                    </div>
                  </div>
                </div>
              </Block>

              {/* Value Proposition */}
              <Block style={{ flex: 1, background: '#fafbff' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                  <IconProp size={64} />
                  <div style={{ flex: 1 }}>
                    <SectionTitle colour="#185FA5" label="Value Proposition" />
                    {str(vp, 'primary_problem_solved') && (
                      <div style={{ marginBottom: '0.5rem' }}>
                        <div style={{ fontSize: '0.55rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#185FA5', opacity: 0.7, marginBottom: '0.15rem' }}>We solve</div>
                        <p style={{ fontSize: '0.78rem', color: '#1a1a2e', margin: 0, lineHeight: 1.5 }}>{str(vp, 'primary_problem_solved')}</p>
                      </div>
                    )}
                    {str(vp, 'target_customer_type') && (
                      <div style={{ marginBottom: '0.5rem' }}>
                        <div style={{ fontSize: '0.55rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#185FA5', opacity: 0.7, marginBottom: '0.15rem' }}>For</div>
                        <p style={{ fontSize: '0.78rem', color: '#1a1a2e', margin: 0, lineHeight: 1.5 }}>{str(vp, 'target_customer_type')}</p>
                      </div>
                    )}
                    {str(vp, 'price_positioning') && <Pill text={str(vp, 'price_positioning')} colour="#185FA5" />}
                  </div>
                </div>
              </Block>
            </div>

            {/* ── COL 3: Customers + Differentiators ──────────────── */}
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {/* Customers */}
              <Block style={{ flex: 1, borderBottom: '1px solid #f5f5f5' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', marginBottom: '0.75rem' }}>
                  <IconCustomer size={64} />
                  <div style={{ flex: 1 }}>
                    <SectionTitle colour="#D97706" label="Customers" />
                    {cust.length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        {cust.slice(0, 4).map((c, i) => {
                          const colours = ['#D97706','#C0556E','#185FA5','#1D9E75'];
                          const col = colours[i % colours.length];
                          return (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                              <div style={{ width: 28, height: 28, borderRadius: '50%', background: col, color: 'white', fontSize: '0.7rem', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                {(c.segment_name || '?')[0]}
                              </div>
                              <div>
                                <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#1a1a2e' }}>{c.segment_name}</div>
                                {c.revenue_pct && <div style={{ fontSize: '0.6rem', color: '#888' }}>{c.revenue_pct} revenue · {c.typical_margin || '—'} margin</div>}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p style={{ fontSize: '0.75rem', color: '#ccc', fontStyle: 'italic', margin: 0 }}>Not yet defined</p>
                    )}
                  </div>
                </div>
              </Block>

              {/* Differentiators */}
              <Block style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                  <IconDiff size={64} />
                  <div style={{ flex: 1 }}>
                    <SectionTitle colour="#7B3AC2" label="What Makes Us Different" />
                    {sortedDiff.length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        {sortedDiff.map((d, i) => {
                          const colours = ['#7B3AC2','#185FA5','#C0556E'];
                          const c = colours[i % colours.length];
                          return (
                            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                              <div style={{ width: 20, height: 20, borderRadius: '50%', background: c, color: 'white', fontSize: '0.6rem', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 2 }}>
                                {d.is_lead === 'Yes' ? '★' : i + 1}
                              </div>
                              <div>
                                <p style={{ fontSize: '0.75rem', color: '#1a1a2e', margin: '0 0 0.15rem', lineHeight: 1.5, fontWeight: d.is_lead === 'Yes' ? 700 : 400 }}>{d.differentiator}</p>
                                {d.defensibility_type && <span style={{ fontSize: '0.58rem', color: c, opacity: 0.8 }}>{d.defensibility_type}</span>}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p style={{ fontSize: '0.75rem', color: '#ccc', fontStyle: 'italic', margin: 0 }}>Not yet defined</p>
                    )}
                  </div>
                </div>
              </Block>
            </div>

            {/* ── COL 4: Go to Market + Current Challenges ────────── */}
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {/* Go to Market */}
              <Block style={{ flex: 1, borderBottom: '1px solid #f5f5f5' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', marginBottom: '0.85rem' }}>
                  <IconGTM size={64} />
                  <div style={{ flex: 1 }}>
                    <SectionTitle colour="#2B7CB0" label="How We Reach Customers" />
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>
                      {[
                        { label: 'Motion', val: gtmPrimaryMotion },
                        { label: 'Sales', val: str(gtm, 'sales_approach') },
                        { label: 'Cycle', val: str(gtm, 'avg_sales_cycle') },
                        { label: 'Retention', val: str(gtm, 'retention_mechanism') },
                      ].filter(r => r.val).map((row, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem' }}>
                          <span style={{ fontSize: '0.55rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#2B7CB0', opacity: 0.7, minWidth: 48, flexShrink: 0 }}>{row.label}</span>
                          <span style={{ fontSize: '0.78rem', color: '#1a1a2e', fontWeight: 600 }}>{row.val}</span>
                        </div>
                      ))}
                      {blendStr && (
                        <div style={{ marginTop: '0.35rem', fontSize: '0.6rem', color: '#888', lineHeight: 1.4 }}>
                          {blendStr}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </Block>

              {/* Current Challenges */}
              <Block style={{ flex: 1, background: '#fffaf8' }}>
                <SectionTitle colour="#C84B1A" label="Current Challenges" />
                {challenges.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.55rem' }}>
                    {challenges.slice(0, 4).map((c, i) => {
                      const urgencyColour = c.urgency === 'Critical' ? '#C84B1A' : c.urgency === 'High' ? '#D97706' : '#6B7A8D';
                      return (
                        <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', paddingBottom: '0.5rem', borderBottom: i < Math.min(challenges.length, 4) - 1 ? '1px solid #f5f5f5' : 'none' }}>
                          <div style={{ width: 8, height: 8, borderRadius: '50%', background: urgencyColour, flexShrink: 0, marginTop: 5 }} />
                          <div style={{ flex: 1 }}>
                            <p style={{ fontSize: '0.75rem', fontWeight: 600, color: '#1a1a2e', margin: '0 0 0.1rem', lineHeight: 1.4 }}>{c.challenge}</p>
                            <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}>
                              {c.urgency && c.urgency !== 'Unclear' && <Pill text={c.urgency} colour={urgencyColour} />}
                              {c.area && <span style={{ fontSize: '0.55rem', color: '#aaa' }}>{c.area}</span>}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p style={{ fontSize: '0.75rem', color: '#ccc', fontStyle: 'italic', margin: 0 }}>No challenges recorded</p>
                )}
              </Block>
            </div>

          </div>

          {/* ── FOOTER ───────────────────────────────────────────── */}
          <div style={{ background: '#1a1a2e', height: 6, opacity: 0.08 }} />
          <div style={{ padding: '0.5rem 2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fafafa', borderTop: '1px solid #f0f0f0' }}>
            <span style={{ fontSize: '0.5rem', color: '#ccc' }}>Generated by GuruOne · InPragmia Consulting · {today}</span>
            <span style={{ fontSize: '0.5rem', color: '#ccc', fontStyle: 'italic' }}>Confidential — for internal use</span>
          </div>

        </div>
      </div>

      <div className="no-print" style={{ textAlign: 'center', padding: '0.75rem', fontSize: '0.75rem', color: '#999' }}>
        Tip: Ctrl+P → Save as PDF → A3 Landscape → Fit to page
      </div>
    </div>
  );
}
